import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useLang, scrollToId } from "@/i18n";
import { CONTACT } from "@/content";

const LINKS = ["about", "services", "booking", "hub", "contact"];

export const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-white/15 font-mono text-xs" data-testid="language-toggle">
      {["ro", "en"].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 uppercase tracking-widest transition-colors duration-300 ${lang === l ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "contact") navigate("/contact");
    else if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled ? "bg-black/70 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"}`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" onClick={() => window.__lenis?.scrollTo(0)}><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((k) => (
            <button key={k} data-testid={`nav-link-${k}`} onClick={() => go(k)} className="relative font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors group">
              {t.nav[k]}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-red-600 transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <button data-testid="nav-cta-button" onClick={() => go("booking")} className="hidden md:inline-flex clip-btn bg-red-600 hover:bg-red-500 transition-colors px-5 py-2.5 font-mono text-xs uppercase tracking-[0.18em] text-white">
            {t.nav.cta}
          </button>
          <button data-testid="mobile-menu-button" aria-label={t.nav.menu} onClick={() => setOpen(true)} className="lg:hidden p-2 text-white"><Menu className="h-6 w-6" /></button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-[#050505] tac-grid flex flex-col px-6 py-5">
            <div className="flex justify-between items-center h-[40px]">
              <Logo testId="mobile-menu-logo" />
              <button data-testid="mobile-menu-close" aria-label={t.nav.close} onClick={() => setOpen(false)} className="p-2 text-white"><X className="h-7 w-7" /></button>
            </div>
            <div className="flex flex-col gap-2 mt-16">
              {LINKS.map((k, i) => (
                <motion.button key={k} data-testid={`mobile-nav-${k}`} initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i + 0.1 }} onClick={() => go(k)} className="text-left font-display font-extrabold uppercase text-5xl text-white hover:text-red-500 transition-colors">
                  <span className="font-mono text-xs text-red-500 mr-3 align-middle">0{i + 1}</span>{t.nav[k]}
                </motion.button>
              ))}
            </div>
            <a href={CONTACT.phoneHref} data-testid="mobile-menu-phone" className="mt-auto flex items-center gap-3 font-mono text-sm text-zinc-300"><Phone className="h-4 w-4 text-red-500" />{CONTACT.phone}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
